import React, { useRef, useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Search, Check, ChevronDown } from 'lucide-react';
import { LW_DS_CLASS } from './LwThemeScope';

export interface FilterOption {
  value: string;
  label: string;
  count: number;
}

/**
 * Multi-select filter popover. Portaled into a scoped `.lw-ds` wrapper and
 * positioned against its trigger (matching the prototype).
 */
export function LwFilterMenu({
  label,
  icon,
  options,
  selected,
  onToggle,
  onClear,
  searchable = false,
  triggerLabel,
  topContent
}: {
  label: string;
  icon: React.ReactNode;
  options: FilterOption[];
  selected: string[];
  onToggle: (value: string) => void;
  onClear: () => void;
  searchable?: boolean;
  triggerLabel?: string;
  topContent?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const btnRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const opts = searchable && q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options;

  return (
    <PopoverPrimitive.Root
      modal={false}
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        setQ('');
      }}
    >
      <PopoverPrimitive.Trigger asChild>
        <button
          ref={btnRef}
          className={`filter-btn${selected.length ? ' has' : ''}${open ? ' open' : ''}`}
          type="button"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (open && event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              setOpen(false);
              btnRef.current?.focus();
            }
          }}
        >
          {icon}
          {triggerLabel ?? label}
          {selected.length > 0 && !triggerLabel && (
            <span className="fcount">{selected.length}</span>
          )}
          <span className="caret">
            <ChevronDown size={13} />
          </span>
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          className={LW_DS_CLASS}
          aria-label={label.toLowerCase().endsWith('filter') ? label : `${label} filter`}
          sideOffset={6}
          align="start"
          collisionPadding={8}
          style={{ zIndex: 50, outline: 'none' }}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            if (searchable) {
              searchRef.current?.focus();
            }
          }}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
            btnRef.current?.focus();
          }}
        >
          <div className="filter-pop" onClick={(event) => event.stopPropagation()}>
            {topContent}
            {searchable && (
              <div className="filter-search">
                <Search size={14} />
                <input
                  ref={searchRef}
                  autoFocus
                  placeholder={`Search ${label.toLowerCase()}…`}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
            )}
            <div className="filter-list">
              {opts.length === 0 && <div className="filter-none">No matches</div>}
              {opts.map((o) => {
                const on = selected.includes(o.value);
                return (
                  <button
                    key={o.value}
                    className={`filter-opt${on ? ' on' : ''}`}
                    onClick={() => onToggle(o.value)}
                    type="button"
                  >
                    <span className="fcheck">{on && <Check size={12} />}</span>
                    <span className="fopt-label">{o.label}</span>
                    <span className="fopt-cnt">{o.count}</span>
                  </button>
                );
              })}
            </div>
            {selected.length > 0 && (
              <button className="filter-clearbtn" onClick={onClear} type="button">
                Clear {label.toLowerCase()}
              </button>
            )}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
